import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {App, Button, Popconfirm, Space, Table, Typography} from 'antd';
import React, {useMemo} from 'react';
import BandPill from '../../../components/common/BandPill';
import {useDeleteValue, useValues} from '../../../hooks/parameters';
import {useScaleBands} from '../../../hooks/useScaleBands';
import {useTranslation} from '../../../hooks/useTranslation';
import {buildValuesQueryParams, getValueRowKey, resolveScaleBandOptionId} from './helpers';
import SectionToolbar from "../../../components/common/SectionToolbar";
import {extractErrorMessage} from '../../../utils/apiError';

const {Text} = Typography;

interface AssessmentOptionProps {
  selectedNodeKey: string | null;
  selectedNodeName: string;
  selectedVariableId: string | number | null;
  selectedVariableGroupId: string | number | null;
  isParentNode: boolean;
  groupName?: string;
  groupAccent?: string;
  groupMono?: string;
  onCreate: () => void;
  onEdit: (record: any) => void;
  drawerOpen: boolean;
}

const formatTpl = (tpl: string, vars: Record<string, string | number>): string =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));

const AssessmentOption: React.FC<AssessmentOptionProps> = ({
                                                             selectedNodeKey,
                                                             selectedNodeName,
                                                             selectedVariableId,
                                                             selectedVariableGroupId,
                                                             isParentNode,
                                                             groupName,
                                                             groupAccent,
                                                             groupMono,
                                                             onCreate,
                                                             onEdit,
                                                             drawerOpen,
                                                           }) => {
  const {t} = useTranslation();
  const {message: messageApi} = App.useApp();

  // Deletes used to `.catch(console.error)`: the backend answered every failure
  // with HTTP 200, so nothing rejected and the user saw no feedback at all.
  // Now that failures arrive as 4xx/5xx, surface the server's message.
  const reportDeleteError = (error: unknown) =>
    messageApi.error(extractErrorMessage(error, t('messages.deleteError')));

  const queryParams = buildValuesQueryParams(
    isParentNode,
    selectedVariableGroupId,
    selectedVariableId,
  );
  const {data: values, isLoading} = useValues(queryParams);
  const {data: scaleBands = []} = useScaleBands();
  const deleteValue = useDeleteValue();

  const bandsById = useMemo(() => {
    const map = new Map<string, any>();
    scaleBands.forEach((b: any) => map.set(String(b.id), b));
    return map;
  }, [scaleBands]);


  const columns = [
    {
      title: t('settings.content.impactAssessment.table.columns.impact'),
      dataIndex: 'scaleBand',
      key: 'scaleBand',
      width: 200,
      render: (_: any, record: any) => {
        const resolved = resolveScaleBandOptionId(record, scaleBands);
        const fullBand = resolved != null ? bandsById.get(String(resolved)) : null;
        const band = fullBand || {
          label: record.scaleBandLabel || record.scaleBand,
          color: record.scaleBandColor || record.color,
          displayOrder: record.scaleBandOrder ?? null,
        };
        return <BandPill band={band}/>;
      },
    },
    ...(!isParentNode ? [{
      title: t('settings.content.impactAssessment.table.columns.name'),
      dataIndex: 'text',
      key: 'text',
      render: (text: string, record: any) => text || record.description || record.name,
    }] : []),
    ...(isParentNode ? [{
      title: t('settings.content.impactAssessment.formulaTable.columns.formula'),
      dataIndex: 'formula',
      key: 'formula',
      render: (formula: string) =>
        formula
          ? <Text code className="gt-ac-formula-cell">{formula}</Text>
          : <span className="gt-ac-muted">—</span>,
    }] : []),
    {
      title: t('settings.content.impactAssessment.table.columns.actions'),
      dataIndex: 'operation',
      width: 100,
      render: (_: any, record: any) => {
        const recordKey = getValueRowKey(record);
        return (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined/>}
              onClick={() => onEdit(record)}
              disabled={!recordKey}
              aria-label={t('settings.content.impactAssessment.table.actions.edit')}
            />
            <Popconfirm
              title={t('settings.content.impactAssessment.table.actions.deleteConfirm')}
              okText={t('settings.content.impactAssessment.formulaTable.actions.delete')}
              cancelText={t('settings.content.impactAssessment.formulaTable.actions.cancel')}
              okButtonProps={{danger: true, loading: deleteValue.isPending}}
              onConfirm={() => deleteValue.mutateAsync(recordKey).catch(reportDeleteError)}
              disabled={!recordKey}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined/>}
                disabled={!recordKey}
                aria-label={t('settings.content.impactAssessment.formulaTable.actions.delete')}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <SectionToolbar
        title={selectedNodeName || ''}
        extra={
          selectedNodeKey ? (
            <Button
              color="primary"
              variant="text"
              size="medium"
              icon={<PlusOutlined/>}
              onClick={onCreate}
              disabled={drawerOpen}
            >
              {t('settings.content.impactAssessment.table.actions.addValue')}
            </Button>
          ) : undefined
        }
      />
      <Table
        size="small"
        bordered
        columns={columns}
        dataSource={values || []}
        loading={isLoading}
        pagination={false}
        rowKey={(record: any) => getValueRowKey(record)}
        locale={{
          emptyText: selectedNodeKey
            ? t('settings.content.impactAssessment.table.messages.noValuesFound')
            : t('settings.content.impactAssessment.table.messages.selectNode'),
        }}
      />
    </>
  );
};

export default AssessmentOption;
